import type { Task } from "@/types";
import { TaskType } from "@/types";

export interface EditableTopicRow {
  /** Stable identity for React keys — added rows have no Congrid id yet. */
  key: string;
  topicId: string | null;
  name: string;
}

/** Topic names are single-line in Congrid, so pasted newlines collapse to spaces. */
export const TOPIC_NAME_NEWLINES = /\s*[\r\n]+\s*/g;

export function rowsFromSubtasks(subtasks: Task[]): EditableTopicRow[] {
  return subtasks
    .filter((subtask) => subtask.taskType === TaskType.INSPECTION_SUBTASK)
    .sort((a, b) => {
      const oa = a.externalOrder ?? "";
      const ob = b.externalOrder ?? "";
      return oa.localeCompare(ob, undefined, { numeric: true });
    })
    .map((subtask) => ({
      key: subtask.id,
      topicId: subtask.externalInspectionPlatformInspectionId ?? null,
      name: subtask.title,
    }));
}

/**
 * `rowsFromSubtasks` plus the one-blank-row fallback so "Lisää tarkastuskohta"
 * is never the only way to start a genuinely empty inspection.
 */
export function seedTopicRows(subtasks: Task[]): EditableTopicRow[] {
  const seeded = rowsFromSubtasks(subtasks);
  return seeded.length > 0
    ? seeded
    : [{ key: crypto.randomUUID(), topicId: null, name: "" }];
}

/** Mirrors the backend's `validate_rows` — at least one row, no blank or duplicate names. */
export function validateTopicRows(rows: EditableTopicRow[]): string | null {
  if (rows.length === 0) {
    return "Lisää vähintään yksi tarkastuskohta.";
  }
  const trimmed = rows.map((row) => row.name.trim());
  if (trimmed.some((name) => !name)) {
    return "Tarkastuskohdan nimi ei voi olla tyhjä.";
  }
  const seen = new Set<string>();
  for (const name of trimmed) {
    const key = name.toLowerCase();
    if (seen.has(key)) {
      return "Tarkastuskohtien nimet eivät voi toistua.";
    }
    seen.add(key);
  }
  return null;
}

/**
 * True when `current` differs from `seeded` by a rename, a reorder, an
 * addition or a removal. Row identity is `key` (the subtask id for a seeded
 * row, a fresh id for one the user added), so a reorder shows up as the same
 * keys in a different position and a rename as the same key with a new name.
 */
export function topicRowsAreDirty(
  seeded: EditableTopicRow[],
  current: EditableTopicRow[],
): boolean {
  if (seeded.length !== current.length) return true;
  return seeded.some(
    (row, index) => row.key !== current[index].key || row.name !== current[index].name,
  );
}

export function updateTopicRowName(
  rows: EditableTopicRow[],
  key: string,
  name: string,
): EditableTopicRow[] {
  return rows.map((row) => (row.key === key ? { ...row, name } : row));
}

export function removeTopicRow(rows: EditableTopicRow[], key: string): EditableTopicRow[] {
  return rows.filter((row) => row.key !== key);
}

export function moveTopicRow(
  rows: EditableTopicRow[],
  index: number,
  direction: -1 | 1,
): EditableTopicRow[] {
  const target = index + direction;
  if (target < 0 || target >= rows.length) return rows;
  const next = [...rows];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function addTopicRow(rows: EditableTopicRow[]): EditableTopicRow[] {
  return [...rows, { key: crypto.randomUUID(), topicId: null, name: "" }];
}
